"use client";

import { useCallback } from "react";
import { GameTenKMRR } from "@/components/dashboard/metrics-games/game-tenk-mrr";
import { TenKGameProvider, useTenKGame } from "@/components/dashboard/metrics-games/tenk-game-context";
import type { EstimatorBenchmark } from "@/lib/types";
import type { TenKGameInputs } from "@/components/dashboard/metrics-games/types";

function tenKInputsFromBenchmark(benchmark: EstimatorBenchmark): Partial<TenKGameInputs> {
  return {
    marketMedianGrowthPerMonth: benchmark.marketMedianGrowthPerMonth,
    nicheMedianMrr: benchmark.nicheMedianMrr,
    nicheTopQuartileMrr: benchmark.nicheTopQuartileMrr,
    nicheMedianArpu: benchmark.nicheMedianArpu,
    sampleSize: Math.min(30, Math.max(1, benchmark.sampleSize))
  };
}

type Props = {
  /** Optional benchmark to pre-populate sliders with real market data (e.g. niche-specific). */
  initialBenchmark?: EstimatorBenchmark;
  /** When true, shows search bar to load startup data. When false, keeps niche context only. */
  searchInCard?: boolean;
  /** Custom title (e.g. "{Niche} $10k MRR challenge" on category page). */
  title?: string;
  /** When set, search and "Load random" are restricted to this category slug only. */
  categoryFilter?: string;
};

export function GameTenKMRRTool({
  initialBenchmark,
  searchInCard = true,
  title,
  categoryFilter
}: Props) {
  const initialTenKInputs = initialBenchmark
    ? tenKInputsFromBenchmark(initialBenchmark)
    : undefined;

  return (
    <TenKGameProvider
      initialTenKInputs={initialTenKInputs}
      categoryFilter={categoryFilter}
    >
      <GameTenKMRRInner searchInCard={searchInCard} title={title} />
    </TenKGameProvider>
  );
}

function GameTenKMRRInner({ searchInCard, title }: { searchInCard: boolean; title?: string }) {
  const ctx = useTenKGame();

  const updateTenKInput = useCallback(
    (key: keyof TenKGameInputs, value: number) => {
      ctx?.setTenKInputs((prev) => ({
        ...prev,
        [key]: Number.isFinite(value) ? value : 0
      }));
    },
    [ctx]
  );

  if (!ctx) return null;

  return (
    <GameTenKMRR
      tenKInputs={ctx.tenKInputs}
      onUpdateTenKInput={updateTenKInput}
      searchInCard={searchInCard}
      title={title}
    />
  );
}
